import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { getAssetLink } from '../../../helpers/get-asset-link'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'

import getSummaryText from './getSummaryText'
import { OccupantType } from './ReservationOccupantInfo'
import { ModifiedPriceType } from './types'
import ReservationPaymentDetailsInfo from './ReservationPaymentDetailsInfo'
import { Avatar } from '@/components/ui/avatar'
import { theme } from '@/theme/theme'

const ReservationSummary = ({
  amenity,
  location,
  calendarValues,
  selectedTime,
  selectedOccupant,
  totalPrice,
  advancedSettings,
  creditCardToken,
  basePrice,
  residentCreditCardData,
  noDiscountPrice,
}) => {
  const { t } = useTranslation(['reservations'])
  const selectedBuilding = useSelector(selectActiveBuilding)
  const reservationFeeEnabled = selectedBuilding?.reservationConvenienceFeeAvailable
  const convenienceFeePercentage =
    parseFloat(selectedBuilding?.residentConvenienceFeeRate) / 100 || 0
  const convenienceFeeValue = reservationFeeEnabled ? totalPrice * convenienceFeePercentage : 0

  const totalPriceToDisplay = (totalPrice + convenienceFeeValue).toFixed(2)

  const creditCardLast4 =
    selectedOccupant && selectedOccupant.type === OccupantType.Guest
      ? selectedOccupant.last4
      : selectedOccupant && residentCreditCardData
        ? residentCreditCardData.last4
        : ''

  const updatedCreditCardLast4 = creditCardToken && creditCardToken.card.last4

  const { modifyPrice, modifyPriceType, modifyPriceValue } = advancedSettings

  const discountApplied =
    modifyPrice &&
    (modifyPriceType === ModifiedPriceType.Discount ||
      modifyPriceType === ModifiedPriceType.DiscountPercentage) &&
    modifyPriceValue

  const discountInfo = discountApplied
    ? modifyPriceType === ModifiedPriceType.DiscountPercentage
      ? `${modifyPriceValue}% ($${(noDiscountPrice - totalPrice).toFixed(2)})`
      : `($${modifyPriceValue})`
    : ''

  const customPriceApplied =
    modifyPrice &&
    modifyPriceType === ModifiedPriceType.CustomPrice &&
    (typeof modifyPriceValue === 'string' || typeof modifyPriceValue === 'number') &&
    modifyPriceValue !== ''

  return (
    <div className="reservation-summary-sidebar">
      <div className="reservation-summary-sidebar__title">
        {t('reservations:reservationSummary.title')}
      </div>
      {amenity && (
        <div className="reservation-summary-sidebar__amenity">
          <Avatar
            alt={amenity.name}
            shape="square"
            size="small"
            zoomable
            src={getAssetLink({
              fallback: { file: 'default_amenity.png' },
              file: location?.image || amenity.image || undefined,
              path: 'chitown-building',
            })}
            style={{ height: theme.space[120], width: theme.space[120] }}
          />
          <div className="reservation-summary-sidebar__amenity-details">
            <div className="reservation-summary-sidebar__amenity-name">
              {location?.name || amenity.name}
            </div>
            <div className="reservation-summary-sidebar__amenity-details-key">
              {t('reservations:reservationSummary.dateAndTime')}
            </div>
            <div className="reservation-summary-sidebar__amenity-details-value">
              {getSummaryText(amenity, calendarValues, selectedTime, true, t)}
            </div>
            <div className="reservation-summary-sidebar__amenity-details-key">
              {t('reservations:reservationSummary.reservationOccupant')}
            </div>
            <div className="reservation-summary-sidebar__amenity-details-value">
              {(selectedOccupant && selectedOccupant.label) || '–'}
            </div>
          </div>
        </div>
      )}
      <div className="reservation-summary-sidebar__separator" />
      <div className="reservation-summary-sidebar__payment-details">
        <div className="reservation-summary-sidebar__payment-details-title">
          {t('reservations:reservationSummary.paymentSummary')}
        </div>
        {totalPrice !== 0 && (
          <ReservationPaymentDetailsInfo
            label={t('reservations:reservationSummary.billingCreditCard')}
            value={
              creditCardLast4 || updatedCreditCardLast4
                ? `**** **** **** ${updatedCreditCardLast4 || creditCardLast4}`
                : '–'
            }
          />
        )}
        <ReservationPaymentDetailsInfo
          label={t('reservations:reservationSummary.baseFee')}
          value={`$${basePrice.toFixed(2)}`}
        />
        {!!amenity.extraFee && (
          <ReservationPaymentDetailsInfo
            label={t('reservations:reservationSummary.oneTimeFee', {
              feeName: amenity.extraFeeName,
            })}
            value={`$${amenity.extraFee.toFixed(2)}`}
          />
        )}
        {reservationFeeEnabled && (
          <ReservationPaymentDetailsInfo
            label={t('reservations:reservationSummary.convenienceFee')}
            value={`$${convenienceFeeValue.toFixed(2)}`}
          />
        )}
        {discountApplied && (
          <ReservationPaymentDetailsInfo
            label={t('reservations:reservationModifyPriceForm.discount')}
            value={discountInfo}
          />
        )}
        <div className="reservation-summary-sidebar__payment-details-separator" />
        <ReservationPaymentDetailsInfo
          label={
            customPriceApplied
              ? t('reservations:reservationSummary.customPrice')
              : t('reservations:reservationSummary.total')
          }
          value={`$${totalPrice.toFixed(2)}`}
          highlightKey
        />
      </div>
    </div>
  )
}

ReservationSummary.propTypes = {
  amenity: PropTypes.object,
  selectedTime: PropTypes.object,
  calendarValues: PropTypes.array,
  selectedOccupant: PropTypes.object,
  discount: PropTypes.number,
  discountPercentage: PropTypes.number,
  customPrice: PropTypes.number,
  totalPrice: PropTypes.number,
  basePrice: PropTypes.number,
  advancedSettings: PropTypes.object,
  creditCardToken: PropTypes.object,
  residentCreditCardData: PropTypes.object,
  noDiscountPrice: PropTypes.number,
  location: PropTypes.object,
}

export default ReservationSummary
